import {Editor, EditorCommand, EditorModeType, PaintData, Point} from "./Editor";
import EditorCommandFactory from "../components/editor/EditorCommandFactory";
import historyUpdaterService, {HistoryProvider} from "./HistoryService";
import {fileEditorStore} from "../stores/FileEditorStore";
import {globalContext} from "../components/editor/GlobalContext";

export type FieldSize = {
    width: number,
    height: number
}

export type FileHistory = {
    fileName: string,
    history: Array<EditorCommand>
}

export class EditorService {
    fileHistory: FileHistory = {
        fileName: "",
        history: []
    }

    editor: Editor

    constructor(editor: Editor) {
        this.editor = editor
    }

    init(fileName: string, initHistory: Array<EditorCommand>, initialZeroOffset: Point) {
        const sameFile = this.fileHistory.fileName === fileName && fileName !== ""
        if (!sameFile) {
            this.editor.clear()
            this.editor.setInitialZeroOffset(initialZeroOffset)
            this.fileHistory = {
                fileName: fileName,
                history: historyUpdaterService.getHistoryFromLocalStorage(fileName)
            }
        } else {
            this.editor.resetForReplay()
        }
        this.editor.applyAllCommands(initHistory)
        this.editor.applyAllCommands(this.fileHistory.history)
        if (!sameFile) {
            this.storeToLS()
        }
        this.editor.processCommands()
        this.syncZeroOffset()
    }

    paintData(): PaintData {
        return this.editor.paintData()
    }

    //
    startLine(point: Point, toolDiameter: number, editorMode: EditorModeType) {
        this.update(EditorCommandFactory.startLine(point, toolDiameter, editorMode))
        this.editor.processCommands()
        this.syncZeroOffset()
    }

    continueLine(point: Point) {
        this.update(EditorCommandFactory.lineTo(point))
        this.editor.processCommands()
        this.syncZeroOffset()
    }

    finishLine() {
        this.update(EditorCommandFactory.finishLine())
        this.editor.processCommands()
        this.syncZeroOffset()
    }

    cancel() {
        this.update(EditorCommandFactory.cancel())
        this.editor.processCommands()
        this.syncZeroOffset()
    }

    unCancel() {
        this.update(EditorCommandFactory.unCancel())
        this.editor.processCommands()
        this.syncZeroOffset()
    }

    drillHole(point: Point, diameter: number) {
        this.update(EditorCommandFactory.addHole(point, diameter))
        this.editor.processCommands()
        this.syncZeroOffset()
    }

    setZeroPoint(point: Point) {
        this.update(EditorCommandFactory.setZero(point))
        this.editor.processCommands()
        this.syncZeroOffset()
    }

    getZeroOffset(): Point | null {
        return this.editor.getZeroOffset()
    }

    //
    private update(editorCommand: EditorCommand) {
        this.fileHistory.history.push(editorCommand)
        this.editor.applyCommand(editorCommand)
        this.storeToLS()
    }

    private syncZeroOffset() {
        const zeroOffset = this.editor.getZeroOffset()
        if (zeroOffset != null) {
            fileEditorStore.setZeroOffsetX(zeroOffset.x)
            fileEditorStore.setZeroOffsetY(zeroOffset.y)
            globalContext.editorContext().zeroOffsetX = zeroOffset.x
            globalContext.editorContext().zeroOffsetY = zeroOffset.y
        }
    }

    private storeToLS() {
        historyUpdaterService.planToUpdateHistory(new HistoryProviderImpl(this.fileHistory))
        historyUpdaterService.planToUpdateHistoryInLocalstorage(new HistoryProviderImpl(this.fileHistory))
    }
}

class HistoryProviderImpl implements HistoryProvider {
    private readonly fileName: string
    private readonly history: Array<EditorCommand>

    constructor(fileHistory: FileHistory) {
        this.fileName = fileHistory.fileName
        this.history = fileHistory.history
    }

    getHistory(): Array<EditorCommand> {
        return [...this.history]
    }

    getFileName(): string {
        return this.fileName
    }
}

const editorService = new EditorService(
    new Editor()
)
export default editorService
